// WHAT: React Context for managing global search state and functionality
// WHY:  Provides centralized search state management with mock search results
// DOMAIN: Shared → Contexts
// DEPENDENCIES: React Context API
// RISKS:
//   - WARN: Uses mock search results - not connected to actual search API
//   - NOTE: performSearch uses hardcoded results - may not match actual routes

import React, { useState, useCallback } from "react";

import { SearchContext, type SearchContextType, type SearchItem } from "./search-context.shared";

interface SearchProviderProperties {
  children: React.ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProperties> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);

  /**
   * WHAT:
   *   Performs search with mock results (not connected to actual search API).
   *
   * WHY:
   *   Placeholder for search functionality - should be replaced with actual API call.
   *
   * BEHAVIOR:
   *   - Returns empty array if query is empty
   *   - Filters hardcoded results by title (case-insensitive)
   *   - Sets search results state
   *
   * RISKS:
   *   - WARN: Uses mock search results - not connected to actual search API
   *   - NOTE: Hardcoded results may not match actual routes
   *   - NOTE: Simple string includes() matching - no fuzzy search or ranking
   */
  const performSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    // WARN: Mock search results - in real app, this would call an API
    const mockResults: SearchItem[] = [
      { id: 1, path: "/dashboard", title: "Dashboard", type: "page" as const },
      { id: 2, path: "/nexus/health/oura", title: "Oura Health", type: "app" as const },
      {
        id: 3,
        path: "/nexus/health/withings",
        title: "Withings Health",
        type: "app" as const,
      },
      { id: 4, path: "/settings", title: "Settings", type: "page" as const },
      { id: 5, path: "/timetracq", title: "TimeTracQ", type: "app" as const },
      { id: 6, path: "/roadmap", title: "Roadmap", type: "app" as const },
    ].filter((item) => item.title.toLowerCase().includes(query.toLowerCase()));

    setSearchResults(mockResults);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchOpen(false);
  }, []);

  const value: SearchContextType = {
    clearSearch,
    isSearchOpen,
    performSearch,
    searchQuery,
    searchResults,
    setIsSearchOpen,
    setSearchQuery,
    setSearchResults,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
